import { Node } from "tiptap";
import { setBlockType } from "tiptap-commands";
import TypographyDropdown from "./TypographyDropdown.vue";

// Configure your typography classes here: each entry becomes an option in
// the toolbar dropdown (replacing the old Paragraph / H1 / H2 dropdown).
// - key:   stored as the node's `variant` attribute
// - label: shown in the dropdown
// - tag:   the HTML tag rendered/parsed for this variant
// - class: the CSS class applied to that tag
//
// "Body" isn't listed here - it's just the plain `paragraph` node (no
// special class), so regular typed text stays a normal <p>. Everything
// below is an explicit style layered on top of that.
export const TYPOGRAPHY_VARIANTS = [
  { key: "statement", label: "Statement", tag: "p", class: "text--statement" },
  { key: "h1", label: "Heading 1", tag: "h1", class: "text--h1" },
  { key: "h2", label: "Heading 2", tag: "h2", class: "text--h2" },
  { key: "h3", label: "Heading 3", tag: "h3", class: "text--h3" },
];

export default class Typography extends Node {
  get name() {
    return "typography";
  }

  get defaultOptions() {
    return {
      variants: TYPOGRAPHY_VARIANTS,
    };
  }

  variantFor(key) {
    return (
      this.options.variants.find((variant) => variant.key === key) ||
      this.options.variants[0]
    );
  }

  get schema() {
    const { variants } = this.options;

    return {
      attrs: {
        variant: { default: variants[0].key },
      },
      content: "inline*",
      group: "block",
      defining: true,
      draggable: false,
      parseDOM: [
        // exact tag + class match, e.g. <h1 class="text--h1"> - this is
        // how content we saved ourselves round-trips. Priority is bumped
        // above the default (50) so it's tried before the plain
        // Paragraph extension's generic `tag: 'p'` rule.
        ...variants.map(({ key, tag, class: className }) => ({
          tag: `${tag}.${className}`,
          attrs: { variant: key },
          priority: 60,
        })),
        // bare-tag fallback for legacy content (e.g. old <h1>/<h2> saved
        // before typography classes existed). Deliberately skipped for
        // "p" tags so an unclassed <p> still parses as a normal
        // paragraph ("Body") instead of being swallowed here.
        ...variants
          .filter(({ tag }) => tag !== "p")
          .filter((v, i, arr) => arr.findIndex((x) => x.tag === v.tag) === i)
          .map(({ key, tag }) => ({ tag, attrs: { variant: key } })),
      ],
      toDOM: (node) => {
        const { tag, class: className } = this.variantFor(node.attrs.variant);
        return [tag, { class: className }, 0];
      },
    };
  }

  commands({ type }) {
    return (attrs) => setBlockType(type, attrs);
  }

  menuBtnView(editorContext) {
    return {
      component: TypographyDropdown,
      componentProps: { editorContext },
    };
  }
}
